import { useEffect, useRef, useState } from "react";
import {
  api,
  ApiError,
  MeetingExpiredError,
  type ExpertSummary,
  type MeetingStateResponse,
  type QaItem,
} from "../api";
import { Desk, type ExpertTone, type MeetingPhase } from "../components/Desk";
import { MeetingTopBar, type MeetingState } from "../components/design/MeetingTopBar";
import { BottomBar } from "../components/design/BottomBar";
import { Icon } from "../components/design/Icon";
import { FinalizingView } from "../components/Finalizing/FinalizingView";
import { FinalizedView } from "../components/Finalized/FinalizedView";
import { RightRail } from "../components/RightRail";
import { NotesPanel } from "../components/NotesPanel";
import { navigate, routes } from "../router";

const POLL_INTERVAL_MS = 3000;

/// `/meetings/<id>` — the single meeting page across all lifecycle states
/// (`in_meeting` → `meeting_over` → `finalizing` → `finalized`). Polls
/// GET /v1/meetings/<id> every 3 s and renders the per-expert Desks layout.
export function Meeting({ meetingId }: { meetingId: string }) {
  const [state, setState] = useState<MeetingStateResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expired, setExpired] = useState<{ expiredAt: string | null } | null>(
    null,
  );
  const liveId = useRef(meetingId);
  liveId.current = meetingId;

  useEffect(() => {
    let cancelled = false;
    async function poll() {
      try {
        const s = await api.getMeeting(liveId.current);
        if (!cancelled) {
          setState(s);
          setError(null);
        }
      } catch (e) {
        if (cancelled) return;
        if (e instanceof MeetingExpiredError) {
          setExpired({ expiredAt: e.expiredAt });
          return;
        }
        setError(
          e instanceof ApiError
            ? `${e.message} (HTTP ${e.status})`
            : e instanceof Error
              ? e.message
              : String(e),
        );
      }
    }
    poll();
    const handle = setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(handle);
    };
  }, [meetingId]);

  if (expired) {
    return (
      <section className="card" style={{ padding: 24, margin: 24 }}>
        <h2 style={{ marginTop: 0 }}>Meeting expired</h2>
        <p className="dim">
          This meeting was auto-deleted
          {expired.expiredAt
            ? ` on ${new Date(expired.expiredAt).toLocaleDateString()}`
            : " after the 7-day retention window"}
          . Drafts, transcripts, and the evidence bundle are no longer
          available.
        </p>
        <div style={{ display: "flex", gap: 12, marginTop: 16 }}>
          <button
            type="button"
            className="btn primary"
            onClick={() => navigate(routes.meetingNew())}
          >
            Start a new meeting
          </button>
          <button
            type="button"
            className="btn"
            onClick={() => navigate(routes.home())}
          >
            Back home
          </button>
        </div>
      </section>
    );
  }

  if (!state && !error) {
    return <p className="dim" style={{ padding: 24 }}>Loading meeting…</p>;
  }
  if (error && !state) {
    return (
      <section className="card" style={{ padding: 24, margin: 24 }}>
        <h2>Meeting unavailable</h2>
        <p style={{ color: "var(--err)" }}>{error}</p>
        <button className="btn" onClick={() => navigate(routes.home())}>
          Back home
        </button>
      </section>
    );
  }
  if (!state) return null;

  return <DesksMeetingView state={state} onStateChange={setState} />;
}

/// Map Recall.ai sub_codes to context-aware operator guidance. Unknown
/// sub_codes fall through to a generic "contact support" hint. Keep
/// the list small + actionable — over-explaining noise tunes operators
/// out. See https://docs.recall.ai/docs/sub-codes for the full taxonomy.
function botErrorHint(subCode: string | undefined): string {
  switch (subCode) {
    case "meeting_requires_sign_in":
    case "google_meet_sign_in_missing_login_credentials":
      return (
        "Your Google Meet requires sign-in. recspec's bot dials in anonymously "
        + "and was rejected. Disable the restriction in your Workspace's Meet "
        + "settings (Admin console → Apps → Google Meet → Meet safety) and "
        + "start a new meeting."
      );
    case "google_meet_organisation_restricted":
      return (
        "This Meet is restricted to members of the host's organization. recspec's "
        + "bot isn't a Workspace user and can't join. Ask the host to allow "
        + "external participants, or wait for signed-in bots to be configured."
      );
    case "google_meet_knocking_disabled":
      return (
        "The Meet's host has disabled knocking, so the bot couldn't request to "
        + "join. Change the meeting's Host management settings to allow "
        + "knocking."
      );
    case "google_meet_login_not_available":
      return (
        "All signed-in bot logins are in use. Try again in a few minutes, or "
        + "ask the operator to provision more login slots in Recall.ai."
      );
    default:
      return (
        "The recording bot couldn't join this meeting. Try creating a fresh "
        + "Meet URL; if the problem persists, send the meeting ID and error "
        + "code below to recspec support."
      );
  }
}

function BotErrorBanner({ err }: { err: NonNullable<MeetingStateResponse["bot_error"]> }) {
  const hint = botErrorHint(err.sub_code);
  return (
    <section
      role="alert"
      style={{
        padding: "var(--space-4) var(--space-5)",
        background: "var(--paper-2)",
        border: "1px solid var(--err)",
        borderLeftWidth: 4,
        borderRadius: "var(--r-3)",
        display: "flex",
        gap: "var(--space-3)",
        alignItems: "start",
      }}
    >
      <div
        aria-hidden="true"
        style={{
          color: "var(--err)",
          fontSize: 20,
          lineHeight: 1,
          marginTop: 2,
        }}
      >
        ⚠
      </div>
      <div style={{ flex: 1 }}>
        <div
          className="serif"
          style={{
            fontSize: 20,
            fontWeight: 400,
            lineHeight: 1.2,
            color: "var(--ink)",
            marginBottom: 4,
          }}
        >
          The recording bot couldn't join the meeting.
        </div>
        <div
          className="dim"
          style={{ font: "var(--type-body-sm)", marginBottom: 8 }}
        >
          {hint}
        </div>
        <details style={{ font: "var(--type-body-sm)", color: "var(--ink-2)" }}>
          <summary style={{ cursor: "pointer" }}>Recall.ai diagnostic</summary>
          <pre
            style={{
              marginTop: 6,
              padding: "var(--space-2)",
              background: "var(--paper-3, var(--paper))",
              borderRadius: "var(--r-2)",
              overflowX: "auto",
              font: "var(--type-mono-sm, monospace)",
            }}
          >
            {`code:       ${err.code}\n`}
            {err.sub_code ? `sub_code:   ${err.sub_code}\n` : ""}
            {`message:    ${err.message}\n`}
            {`detected:   ${new Date(err.detected_at).toLocaleString()}`}
          </pre>
        </details>
      </div>
    </section>
  );
}

function DesksMeetingView({
  state,
  onStateChange,
}: {
  state: MeetingStateResponse;
  onStateChange: (s: MeetingStateResponse) => void;
}) {
  const [experts, setExperts] = useState<ExpertSummary[]>([]);
  const [finalizeBusy, setFinalizeBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [activeSpeakerIdx, setActiveSpeakerIdx] = useState(0);

  useEffect(() => {
    api.listExperts().then(setExperts).catch(() => {});
  }, []);

  const phase: MeetingPhase = state.state;
  const expertsById = new Map(experts.map((e) => [e.id, e]));
  const myExperts = state.expert_ids
    .map((pin) => pin.split("@")[0])
    .map((id) => expertsById.get(id))
    .filter((e): e is ExpertSummary => e !== undefined);

  // Active-speaker rotation. When the meeting is live and voice is on,
  // cycle the visual "speaking" indicator across picked Desks every 4.2 s.
  // This is a placeholder until the worker reports real bot-speaker state.
  const voiceOn = state.channels.voice !== "off" && phase === "in_meeting";
  useEffect(() => {
    if (!voiceOn || myExperts.length === 0) return;
    const handle = setInterval(() => {
      setActiveSpeakerIdx((i) => (i + 1) % myExperts.length);
    }, 4200);
    return () => clearInterval(handle);
  }, [voiceOn, myExperts.length]);

  async function finalize() {
    setFinalizeBusy(true);
    setActionError(null);
    try {
      await api.finalizeMeeting(state.meeting_id);
      const fresh = await api.getMeeting(state.meeting_id);
      onStateChange(fresh);
    } catch (e) {
      setActionError(
        e instanceof ApiError
          ? `${e.message} (HTTP ${e.status})`
          : e instanceof Error
            ? e.message
            : String(e),
      );
      // Only release the busy flag on actual finalize-dispatch failure.
      // The happy path keeps `finalizeBusy = true` until the polling
      // useEffect below sees state.state flip to "finalized" — the
      // worker takes 2–5 min and we want the FinalizingView visible
      // the whole time.
      setFinalizeBusy(false);
    }
  }

  // Once finalize is dispatched, hold the FinalizingView until the
  // doc actually transitions. Without this, finalizeBusy clears the
  // moment the dispatch POST returns and the user sees the meeting-over
  // desk view for the 2–5 minutes the worker is actually working.
  useEffect(() => {
    if (state.state === "finalized" && finalizeBusy) {
      setFinalizeBusy(false);
    }
  }, [state.state, finalizeBusy]);

  // Detect "worker is actively finalizing" from doc state alone (covers
  // page refreshes mid-finalize): any expert_progress with `running` or
  // `done` while the meeting is still in `meeting_over`.
  const workerFinalizing =
    state.state === "meeting_over"
    && state.expert_progress != null
    && Object.values(state.expert_progress).some(
      (s) => s === "running" || s === "done",
    );
  const showFinalizingView = finalizeBusy || workerFinalizing;

  // The lifecycle pill maps the api's ("in_meeting"|"meeting_over"|"finalized")
  // onto the design's four states. The "finalizing" pill is shown while a
  // user-initiated finalize POST is in flight (the api currently transitions
  // directly from meeting_over to finalized — finalizing is a SPA-side
  // intermediate visible during the 2–5 min worker pipeline window).
  const topBarState: MeetingState = showFinalizingView ? "finalizing" : state.state;
  const elapsed = useElapsed(state.joined_at ?? state.created_at);
  // `updateChannels` was removed when the in-page voice three-state
  // control retired with `introduce-approval-queue-concept`. The
  // PATCH /v1/meetings/{id}/channels endpoint is still wired and
  // covers `qa` + `frames` toggles, but the SPA doesn't expose a UI
  // for either in the new layout. Re-add a thin wrapper if/when a
  // mid-meeting toggle surface lands.

  return (
    <div
      style={{
        background: "var(--paper)",
        color: "var(--ink)",
        font: "var(--type-body)",
        display: "grid",
        gridTemplateRows: "auto 1fr auto",
        minHeight: "100%",
      }}
    >
      <MeetingTopBar
        meetingId={state.meeting_id.slice(0, 12)}
        state={topBarState}
        elapsed={topBarState !== "finalized" ? elapsed : undefined}
        right={
          <>
            <button
              type="button"
              className="btn sm"
              onClick={() => navigate(routes.transcript(state.meeting_id))}
            >
              <Icon name="transcript" size={13} /> Transcript
            </button>
            <button
              type="button"
              className="btn sm"
              onClick={() => navigate(routes.frames(state.meeting_id))}
            >
              <Icon name="frames" size={13} /> Frames
            </button>
            {(phase === "in_meeting" || phase === "meeting_over") &&
              !finalizeBusy && (
                <button
                  type="button"
                  className="btn primary sm"
                  onClick={finalize}
                  title={
                    phase === "in_meeting"
                      ? "End the meeting and assemble the bundle"
                      : "Assemble the bundle"
                  }
                >
                  Finalize <Icon name="arrowR" size={12} />
                </button>
              )}
            {finalizeBusy && (
              <button type="button" className="btn sm" disabled>
                <Icon name="download" size={13} /> Bundle (preparing…)
              </button>
            )}
            {phase === "finalized" && (
              <>
                <button
                  type="button"
                  className="btn sm"
                  onClick={() => {
                    const url = `${window.location.origin}${routes.meeting(state.meeting_id)}`;
                    navigator.clipboard?.writeText(url);
                  }}
                  title="Copy meeting URL to clipboard"
                >
                  <Icon name="copy" size={13} /> Copy share link
                </button>
                {state.resolved_job_id && (
                  <a
                    className="btn primary sm"
                    href={`/v1/jobs/${encodeURIComponent(state.resolved_job_id)}/download`}
                    style={{ textDecoration: "none" }}
                  >
                    <Icon name="download" size={13} /> Download bundle (.zip)
                  </a>
                )}
              </>
            )}
          </>
        }
      />

      {showFinalizingView ? (
        <FinalizingView
          experts={myExperts}
          expertProgress={state.expert_progress}
          agent={state.agent}
        />
      ) : phase === "finalized" ? (
        <FinalizedView state={state} experts={experts} />
      ) : (
      <div
        style={{
          padding: "var(--space-5)",
          overflow: "auto",
        }}
      >
        <div
          style={{
            maxWidth: 1280,
            margin: "0 auto",
            display: "grid",
            gap: "var(--space-4)",
          }}
        >
          {state.bot_error && <BotErrorBanner err={state.bot_error} />}

          {actionError && (
            <div style={{ color: "var(--err)", font: "var(--type-body-sm)" }}>
              {actionError}
            </div>
          )}

          {phase === "meeting_over" && (
            <section
              style={{
                padding: "var(--space-4) var(--space-5)",
                background: "var(--paper-2)",
                border: "1px solid var(--rule)",
                borderRadius: "var(--r-3)",
                display: "flex",
                alignItems: "center",
                gap: "var(--space-4)",
              }}
            >
              <div style={{ flex: 1 }}>
                <div className="serif" style={{ fontSize: 22, fontWeight: 400, lineHeight: 1.1 }}>
                  Meeting wrapped.
                </div>
                <div className="dim" style={{ fontSize: 13, marginTop: 4 }}>
                  Click Finalize to run each expert's pipeline and assemble the
                  bundle. Typically 2–5 minutes.
                </div>
              </div>
              <button
                type="button"
                className="btn primary"
                onClick={finalize}
                style={{ padding: "10px 18px", fontSize: 14 }}
              >
                Finalize <Icon name="arrowR" size={13} />
              </button>
            </section>
          )}

          <div
            className="recspec-desks"
            style={{
              display: "grid",
              gridTemplateColumns:
                myExperts.length > 1
                  ? "repeat(auto-fit, minmax(420px, 1fr))"
                  : "1fr",
              gap: "var(--space-4)",
            }}
          >
            {myExperts.length === 0 && (
              <div className="dim">Loading experts…</div>
            )}
            {myExperts.map((e, i) => {
              const tone: ExpertTone =
                (state.expert_tones?.[e.id] as ExpertTone | undefined) ??
                "consultant";
              const expertQa = state.qa_items.filter(
                (q) => q.expert_id === e.id,
              );
              // Surface the agent's archetype + axes on this desk when
              // it matches this expert's bare id (single-agent meetings
              // always do; legacy two-expert meetings only the first
              // does — the second renders without the archetype chip).
              const agentForDesk =
                state.agent && state.agent.expert_id === e.id ? state.agent : undefined;
              const archetypeDisplayName = agentForDesk
                ? e.archetypes?.find((a) => a.id === agentForDesk.archetype_id)
                    ?.display_name
                : undefined;
              return (
                <Desk
                  key={e.id}
                  meetingId={state.meeting_id}
                  expert={e}
                  tone={tone}
                  phase={phase}
                  qaItems={expertQa}
                  language={state.language}
                  agent={agentForDesk}
                  archetypeDisplayName={archetypeDisplayName}
                  speaking={voiceOn && activeSpeakerIdx === i}
                  onToneChange={(next) =>
                    onStateChange({
                      ...state,
                      expert_tones: { ...state.expert_tones, [e.id]: next },
                    })
                  }
                />
              );
            })}
          </div>
        </div>

        {/* Q&A panel center-stage — full width on a soft surface, capped
            at 820 px for readability per the design's redesigned in-meeting
            layout (no right rail, no bot tile preview, no Live/Draft tabs). */}
        <section
          style={{
            borderTop: "1px solid var(--rule)",
            background: "var(--paper-2)",
            padding: "var(--space-4) var(--space-5)",
          }}
        >
          <div
            style={{
              maxWidth: 820,
              width: "100%",
              margin: "0 auto",
            }}
          >
            {/* Center-stage panel switch: the Notetaker's live notes
                replace the Q&A panel when this meeting picked the
                notetaker expert. With today's 1-expert cap exactly
                one of them is the meeting's active expert, so we
                render the panel that matches — running both at once
                would just give the inactive one an empty
                placeholder. */}
            {(() => {
              const notetakerEnabled = state.expert_ids.some((p) =>
                p.startsWith("notetaker@"),
              );
              if (notetakerEnabled) {
                const slot = state.live_state?.notetaker;
                const notes =
                  slot && slot.value
                    ? (slot.value as import("../api").NotesArtifact)
                    : undefined;
                return (
                  <NotesPanel
                    meetingId={state.meeting_id}
                    notes={notes}
                    updatedAt={slot?.updated_at}
                    enabled={true}
                    onNotesPatched={(nextItem) => {
                      // Optimistic local swap so the UI reflects the
                      // PATCH result without waiting for the next poll.
                      const current =
                        slot && slot.value
                          ? (slot.value as import("../api").NotesArtifact)
                          : undefined;
                      if (!current) return;
                      const action_items = current.action_items.map((a) =>
                        a.id === nextItem.id ? nextItem : a,
                      );
                      onStateChange({
                        ...state,
                        live_state: {
                          ...(state.live_state ?? {}),
                          notetaker: {
                            ...slot!,
                            value: { ...current, action_items },
                          },
                        },
                      });
                    }}
                  />
                );
              }
              return (
                <RightRail
                  meetingId={state.meeting_id}
                  experts={experts}
                  agent={state.agent}
                  channels={state.channels}
                  qaItems={state.qa_items}
                  liveQuestions={state.live_questions}
                  phase={phase}
                  onQaItemsChange={(qa_items: QaItem[]) =>
                    onStateChange({ ...state, qa_items })
                  }
                />
              );
            })()}
          </div>
        </section>
      </div>
      )}

      <BottomBar
        left={
          <span className="dim mono" style={{ fontSize: 11 }}>
            owner @{state.owner_login}
          </span>
        }
        right={
          <span className="dim mono" style={{ fontSize: 11 }}>
            press <span className="kbd">⌘ .</span> to finalize
          </span>
        }
      />
    </div>
  );
}

function useElapsed(startedAt: string | null): string {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!startedAt) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [startedAt]);
  if (!startedAt) return "00:00";
  const diff = Math.max(0, now - new Date(startedAt).getTime());
  const total = Math.floor(diff / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => n.toString().padStart(2, "0");
  return h > 0 ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}
